import type { LinkedInCredential } from './types';

/**
 * Whether a LinkedIn account is usable, and which one a screen should act on.
 *
 * <h2>The single definition of "usable"</h2>
 *
 * It must stay identical to the server's own check, which every agent operation runs before it
 * touches LinkedIn: **`isActive` AND `loginStatus` is `active`** (backend LA-4, LA-5, LA-16).
 * Anything looser announces a connection the server refuses the moment the agent runs. The
 * previous system's connect screens used `isActive || loginStatus === 'active'` while its status
 * hook used `loginStatus` alone, which is how "connected here, not connected there" happened.
 */
export type CredentialState = 'connected' | 'stale' | 'pending' | 'none';

const loginStatusOf = (credential: LinkedInCredential): string =>
  credential.loginStatus.toLowerCase().trim();

/** Whether the signed-in person may log the account in again: they are the one who connected it. */
export function isCredentialYours(credential: LinkedInCredential | null | undefined): boolean {
  return !credential || credential.connectedByYou !== false;
}

/** What a colleague is told where the person who connected the account would see Reconnect. */
export const ONLY_THE_CONNECTOR_RECONNECTS =
  'Only the person who connected this account can reconnect it.';

/** True only for an account the server will actually accept. */
export function isCredentialConnected(credential: LinkedInCredential | null | undefined): boolean {
  return !!credential && credential.isActive && loginStatusOf(credential) === 'active';
}

/**
 * Where an account stands.
 *
 * <ul>
 *   <li>`connected` — usable right now;</li>
 *   <li>`stale` — was connected once and the session has since died: offer Reconnect;</li>
 *   <li>`pending` — created and never finished: resume it, never create a second row;</li>
 *   <li>`none` — no account, or one that was disconnected (`revoked`).</li>
 * </ul>
 */
export function resolveCredentialState(
  credential: LinkedInCredential | null | undefined,
): CredentialState {
  if (!credential) return 'none';
  if (isCredentialConnected(credential)) return 'connected';

  const status = loginStatusOf(credential);
  if (status === 'expired' || status === 'failed' || status === 'timeout') return 'stale';
  // `active` without `isActive` is a contradiction the server rejects — an account whose cookies
  // were replaced, say. Treated as stale so the person is offered a reconnect and not a
  // connection that cannot work.
  if (status === 'active') return 'stale';
  if (status === 'revoked') return 'none';
  return 'pending';
}

/**
 * The one account to act on: connected first, then stale, then pending, **newest first inside
 * each tier**, so this agrees with the server's own newest-first choice. Without the ordering the
 * screen could poll one row while the agent used another.
 */
export function pickCredential(
  credentials: readonly LinkedInCredential[],
): LinkedInCredential | null {
  if (credentials.length === 0) return null;

  const timeOf = (credential: LinkedInCredential): number => {
    const parsed = Date.parse(credential.createdAt);
    return Number.isNaN(parsed) ? 0 : parsed;
  };
  const newestFirst = [...credentials].sort((a, b) => timeOf(b) - timeOf(a));

  for (const tier of ['connected', 'stale', 'pending'] as const) {
    const match = newestFirst.find((entry) => resolveCredentialState(entry) === tier);
    if (match) return match;
  }
  return null;
}
